import classNames from 'classnames';
import { identity, includes, take } from 'lodash';
import { debounce } from 'lodash/fp';
import PropTypes from 'prop-types';
import type * as React from 'react';
import { Component, Fragment } from 'react';
import * as keyEventStack from '../..//services/KeyEventStack';
import { getNext, getPrevious } from '../../services/List';
import { AriaAnnouncer } from '../AriaHelper';
import Arrow from '../Arrow';
import Button from '../Button';
import Input from '../Input';
import styles from './style.m.less';

const EMPTY_ITEM = {};
const EMPTY_STYLE = {};

const MAX_CACHE_SIZE = 20;

export function Choice({ label, isHighlighted }: { label: React.ReactNode; isHighlighted?: boolean }) {
    return <div className={classNames(styles.choice, { [styles.isHighlighted]: isHighlighted })}>{label}</div>;
}

export function choiceWithLabel<T>(choiceToLabel: (choice: T) => React.ReactNode) {
    return (choice: T, isHighlighted?: boolean) => (
        <Choice label={choiceToLabel(choice)} isHighlighted={isHighlighted} />
    );
}

interface GetKey<T = any> {
    (result: T, i: number): number | string;
}

interface Props<T> {
    query: string;
    onSelect: (result: T) => void;
    clearOnSelect: boolean;
    replaceQueryOnSelect: boolean;
    quickSelectItems: T[];
    queryFn: (query: string) => Promise<T[]>;
    searchOnEmptyQuery?: boolean;
    children: (result: T, isHighlighted?: boolean) => React.ReactElement;

    className?: string;
    id?: string;
    maxItemsShown?: number;
    getKey?: GetKey<T>;
    itemHeight?: string;
    inputStyle?: string;
    resultsStyle?: string;
    resultsContainerStyle?: string;
    placeholder?: string;
    error?: string;
    autoFocus?: boolean;
    filterResults?: (results: T[], query: string) => T[];
    nothingFoundText?: string;
    onBlur?: (ev: React.FocusEvent<HTMLInputElement>) => void;
    onFocus?: () => void;
    groupQuickSelect?: (results: T[]) => ResultGroup<T>[];
    showQuickSelectArrow?: boolean;
    disabled?: boolean;
    dataIntercomTarget?: string;
}

interface State<T> {
    query: string;
    showResults: boolean;
    results: T[];
    highlightedResult?: T | null;
    quickSelectMode: boolean;
}

interface Cache<T> {
    dict: { [key: string]: T[] };
    list: string[];
}

export default class Autocomplete<T> extends Component<Props<T>, State<T>> {
    static defaultProps: Partial<Props<unknown>> = {
        filterResults: t => t,
        maxItemsShown: 7,
        clearOnSelect: true,
        replaceQueryOnSelect: false,
        searchOnEmptyQuery: false,
        getKey: (t, i) => i,
        quickSelectItems: [],
        showQuickSelectArrow: true,
        nothingFoundText: 'Nothing found',
        disabled: false,
    };

    static propTypes = {
        id: PropTypes.string,
        queryFn: PropTypes.func.isRequired, // (query) => Promise<T[]>
        filterResults: PropTypes.func, // (T[]) => T[]
        onSelect: PropTypes.func.isRequired, // (T) => void
        clearOnSelect: PropTypes.bool,
        replaceQueryOnSelect: PropTypes.bool,
        searchOnEmptyQuery: PropTypes.bool,
        maxItemsShown: PropTypes.number,
        children: PropTypes.func.isRequired, // (T, isSelected: boolean) => Component
        getKey: PropTypes.func, // (T, i) => key
        itemHeight: PropTypes.number,
        quickSelectItems: PropTypes.array, // T[] - will immediately pop up the completion menu, before you start typing
        groupQuickSelect: PropTypes.func, // (T[]) => { label: string | undefined, results: T[] }[]
        showQuickSelectArrow: PropTypes.bool,
        query: PropTypes.string,

        nothingFoundText: PropTypes.string,

        placeholder: PropTypes.string,
        error: PropTypes.string,
        inputStyle: PropTypes.string,
        resultsStyle: PropTypes.string,
        resultsContainerStyle: PropTypes.string,
        className: PropTypes.string,

        autoFocus: PropTypes.bool,
        dataIntercomTarget: PropTypes.string,
    };

    state: State<T> = {
        query: this.props.query ?? '',
        showResults: false,
        results: [],
        highlightedResult: undefined,
        quickSelectMode: false,
    };

    cache: Cache<T> = {
        dict: {},
        list: [],
    };

    modalStackCallback: unknown;

    keyEventStackCallback = {
        onKey: (event: React.KeyboardEvent) => {
            if (keyEventStack.isEsc(event)) {
                this.hideResults();
                return false;
            }
        },
    };

    hasRegisteredKeyEventStackCallback = false;

    node: HTMLElement | null = null;
    inputNode: HTMLInputElement | null = null;

    initialRender = true;

    componentDidMount() {
        this.initialRender = false;
    }

    componentWillUnmount() {
        if (this.hasRegisteredKeyEventStackCallback) {
            keyEventStack.deregister(this.keyEventStackCallback);
        }
    }

    setResults(results: T[], isQuickSelection = false) {
        if (!this.hasRegisteredKeyEventStackCallback && this.state.query !== '') {
            this.hasRegisteredKeyEventStackCallback = true;
            keyEventStack.register(this.keyEventStackCallback);
        }
        this.setState({ results, showResults: true, quickSelectMode: isQuickSelection });
    }

    updateCache(query: string, results: T[]) {
        const { list, dict } = this.cache;
        list.push(query);
        dict[query] = results;
        if (list.length > MAX_CACHE_SIZE) {
            const toDiscard = list.shift();
            delete dict[toDiscard!];
        }
    }

    hideResults = () => {
        this.setState({ showResults: false, quickSelectMode: false, highlightedResult: undefined });
        this.hasRegisteredKeyEventStackCallback = false;
        keyEventStack.deregister(this.keyEventStackCallback);
    };

    interceptKeyboardHandling = (event: React.KeyboardEvent, results: T[]) => {
        const { highlightedResult } = this.state;

        const actions: { [keyCode: number]: { prevent: boolean; action(): void } | undefined } = {
            13: {
                // enter
                prevent: true,
                action: () => {
                    if (highlightedResult) {
                        this.onSelect(highlightedResult);
                    }
                },
            },
            27: {
                prevent: false,
                action: () => this.hideResults(),
            },
            32: {
                // space
                prevent: false,
                action: () => {
                    if (highlightedResult) {
                        this.onSelect(highlightedResult);
                    }
                },
            },
            38: {
                // up arrow
                prevent: true,
                action: () => this.highlightPrevious(results),
            },
            40: {
                // down arrow
                prevent: true,
                action: () => this.openResultsOrHighlightNext(results),
            },
        };
        const action = actions[event.keyCode];
        if (action) {
            action.action();
            if (action.prevent) {
                event.preventDefault();
                event.stopPropagation();
            }
        }
    };

    onHighlight = (result?: T | null) => {
        this.setState({ highlightedResult: result });
    };

    openResultsOrHighlightNext(results: T[]) {
        if (!this.state.showResults) {
            this.showResultDropdown();
        } else {
            this.highlightNext(results);
        }
    }

    highlightNext(results: T[]) {
        const { highlightedResult } = this.state;
        this.onHighlight(getNext(results, highlightedResult));
    }

    highlightPrevious(results: T[]) {
        const { highlightedResult } = this.state;
        this.onHighlight(getPrevious(results, highlightedResult));
    }

    clear(callback?: () => void) {
        this.setState(
            {
                query: '',
                highlightedResult: undefined,
                results: [],
                showResults: false,
            },
            callback
        );
    }

    onSelect = (result: T, ev?: React.SyntheticEvent) => {
        const { onSelect, clearOnSelect, replaceQueryOnSelect } = this.props;
        if (ev) {
            ev.preventDefault();
        }
        if (clearOnSelect || replaceQueryOnSelect) {
            this.clear(this.focus);
            if (this.hasRegisteredKeyEventStackCallback) {
                this.hasRegisteredKeyEventStackCallback = false;
                keyEventStack.deregister(this.keyEventStackCallback);
            }

            if (replaceQueryOnSelect) {
                if (typeof result !== 'string') throw new Error('Result is not a string');
                this.setState({ query: result });
            }
        } else {
            this.focus();
        }

        onSelect(result);
    };

    focus = () => {
        if (this.inputNode) {
            this.inputNode.focus();
        }
    };

    showResultDropdown = () => {
        if (this.initialRender) {
            return;
        }
        const { query } = this.state;

        if (!query) {
            this.tryQuickSelections();
        } else {
            this.setState({
                showResults: true,
            });
        }
    };

    tryQuickSelections() {
        if (this.props.quickSelectItems.length) {
            this.setResults(this.props.quickSelectItems, true);
        }
    }

    search(query: string) {
        const cached = this.cache.dict[query];
        if (cached) {
            this.setResults(cached);
        } else {
            const { queryFn } = this.props;
            queryFn(query).then(results => {
                this.updateCache(query, results);
                if (this.state.query) {
                    this.setResults(results);
                }
            });
        }
    }

    debouncedSearch = debounce(500, query => {
        this.search(query);
    });

    onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        const query = event.target.value;
        if (!query && !this.props.searchOnEmptyQuery) {
            if (this.hasRegisteredKeyEventStackCallback) {
                this.hasRegisteredKeyEventStackCallback = false;
                keyEventStack.deregister(this.modalStackCallback);
            }
            this.clear();
            return;
        }
        this.setState({ query });
        this.debouncedSearch(query);
    };

    render() {
        const {
            id,
            className,
            children,
            maxItemsShown,
            getKey,
            itemHeight,
            inputStyle,
            resultsStyle,
            resultsContainerStyle,
            placeholder,
            error,
            autoFocus,
            filterResults,
            nothingFoundText,
            onBlur,
            onFocus,
            quickSelectItems,
            groupQuickSelect,
            showQuickSelectArrow,
            disabled,
            dataIntercomTarget,
        } = this.props;
        const { query, showResults, results, highlightedResult, quickSelectMode } = this.state;

        const filteredResults = filterResults!(results, query); // ! has default prop
        const shownResults = take(filteredResults, maxItemsShown);
        const notShownResultsCount = filteredResults.length - maxItemsShown!; // ! has default prop

        const inputProps = {
            id,
            value: query,
            onChange: this.onChange,
            onKeyDown: (ev: React.KeyboardEvent) => this.interceptKeyboardHandling(ev, shownResults),
            className: inputStyle,
            placeholder,
            error,
            autoFocus,
            onClick: this.showResultDropdown,
            onFocus: () => {
                if (onFocus) {
                    onFocus();
                }
                this.showResultDropdown();
            },
            onBlur: (ev: React.FocusEvent<HTMLInputElement>) => {
                // !node when this is triggered, the node should be present
                if (!this.node!.contains(ev.relatedTarget as Node)) {
                    this.hideResults();
                }
                (onBlur || identity)(ev);
            },
            getRef: (inputNode: HTMLInputElement | null) => (this.inputNode = inputNode),
            autoComplete: 'off',
            disabled: disabled,
            dataIntercomTarget,
        };

        const resultsProps: ResultsProps<T> = {
            results: shownResults,
            showResults,
            notShownResultsCount,
            highlightedResult,
            onSelect: this.onSelect,
            onHighlight: this.onHighlight,
            renderItem: children,
            getKey: getKey!, // ! getKey is in default props
            itemHeight,
            resultsStyle,
            resultsContainerStyle,
            quickSelectMode,
            groupQuickSelect,
            nothingFoundText,
        };

        const ariaAnnouncementProps: AriaAnnouncementProps<T> = {
            results: shownResults,
            notShownResultsCount,
            highlightedResult,
            renderItem: children,
            showResults,
        };

        return (
            <div className={className} ref={node => (this.node = node)}>
                {showQuickSelectArrow && quickSelectItems.length ? (
                    <div className={styles.inputContainer}>
                        <Input {...inputProps} />
                        <div className={styles.arrow}>
                            <Arrow dir="DOWN" />
                        </div>
                    </div>
                ) : (
                    <Input {...inputProps} />
                )}
                <Results<T> {...resultsProps} />
                <AriaAnnouncer>
                    <AriaAnnouncement<T> {...ariaAnnouncementProps} />
                </AriaAnnouncer>
            </div>
        );
    }
}

interface ResultGroup<T> {
    results: T[];
    key: string;
    label?: string;
}

interface ResultsProps<T> {
    results: T[];
    showResults?: boolean;
    notShownResultsCount: number;
    highlightedResult?: T | null;
    onSelect: (result: T, ev: React.SyntheticEvent) => void;
    onHighlight: (result: T) => void;
    renderItem: (result: T, isHighlighted: boolean) => React.ReactNode;
    getKey: GetKey;
    itemHeight?: string;
    resultsStyle?: string;
    resultsContainerStyle?: string;
    quickSelectMode?: boolean;
    groupQuickSelect?: (results: T[]) => ResultGroup<T>[];
    nothingFoundText?: string;
}

function Results<T>({
    results,
    showResults,
    notShownResultsCount,
    highlightedResult,
    onSelect,
    onHighlight,
    renderItem,
    getKey,
    itemHeight,
    resultsStyle,
    resultsContainerStyle,
    quickSelectMode,
    groupQuickSelect,
    nothingFoundText,
}: ResultsProps<T>) {
    const resultsClasses = classNames(styles.results, resultsStyle, {
        [styles.results_empty]: !(results.length && showResults),
    });
    const containerClasses = classNames(styles.results_container, resultsContainerStyle);

    const resultGroups =
        quickSelectMode && groupQuickSelect ? groupQuickSelect(results) : [{ results: results, key: 'default' }];

    const resultsInnerProps = {
        showResults,
        highlightedResult,
        onSelect,
        onHighlight,
        renderItem,
        getKey,
        itemHeight,
        quickSelectMode,
        nothingFoundText,
    };

    return (
        <div className={containerClasses}>
            <div className={resultsClasses}>
                <div className={styles.results_inner}>
                    {resultGroups.map(resultGroup => (
                        <Fragment key={resultGroup.key || resultGroup.label}>
                            {resultGroup.label && <h3 className={styles.resultGroupHeading}>{resultGroup.label}</h3>}
                            <ResultsInner {...resultsInnerProps} results={resultGroup.results} />
                        </Fragment>
                    ))}
                </div>
                <MoreItems count={notShownResultsCount} show={showResults} />
            </div>
        </div>
    );
}

interface ResultsInnerProps<T> {
    results: T[];
    showResults?: boolean;
    highlightedResult?: T | null;
    onSelect: (result: T, ev: React.SyntheticEvent) => void;
    onHighlight: (result: T) => void;
    renderItem: (result: T, isHighlighted: boolean) => React.ReactNode;
    getKey: GetKey;
    itemHeight?: string;
    quickSelectMode?: boolean;
    nothingFoundText?: string;
}

function isEmptyItem<T>(item: T | {}): item is {} {
    return item === EMPTY_ITEM;
}

function ResultsInner<T>({
    results,
    showResults,
    highlightedResult,
    onSelect,
    onHighlight,
    renderItem,
    getKey: originalGetKey,
    itemHeight,
    quickSelectMode,
    nothingFoundText,
}: ResultsInnerProps<T>) {
    const getKey = prepareGetKey(originalGetKey);
    const render = (result: T | typeof EMPTY_ITEM, key: string | number) => {
        if (isEmptyItem(result)) {
            return nothingFoundText ? <NothingFound key="nothing" text={nothingFoundText} height={itemHeight} /> : null;
        }
        const isHighlighted = result === highlightedResult;
        const props = {
            onMouseEnter: () => onHighlight(result),
            onMouseDown: (ev: React.MouseEvent) => onSelect(result, ev),
            key: key,
            className: styles.item,
        };
        return (
            <Button kind="PLAIN" {...props} tabIndex={-1}>
                {renderItem(result, isHighlighted)}
            </Button>
        );
    };
    const items = prepareItems(results, showResults, quickSelectMode);

    return <>{items.map((item, i) => render(item, getKey(item, i)))}</>;
}

function NothingFound({ height, text }: { height?: string; text: React.ReactNode }) {
    const style = height ? { height } : EMPTY_STYLE;
    return (
        <div className={styles.nothing_found} style={style}>
            {text}
        </div>
    );
}

function MoreItems({ count, show }: { count: number; show?: boolean }) {
    if (count <= 0 || !show) {
        return null;
    }
    return <div className={styles.more_items}>and {count} more</div>;
}

interface AriaAnnouncementProps<T> {
    showResults?: boolean;
    results: T[];
    renderItem: (result: T) => React.ReactElement;
    highlightedResult?: T | null;
    notShownResultsCount: number;
}

function AriaAnnouncement<T>({
    showResults,
    results,
    renderItem,
    highlightedResult,
    notShownResultsCount,
}: AriaAnnouncementProps<T>) {
    if (!showResults) {
        return null;
    }

    if (highlightedResult && includes(results, highlightedResult)) {
        return renderItem(highlightedResult);
    }

    return <div>{getResultDescription(results, notShownResultsCount)}</div>;
}

function getResultDescription<T>(results: T[], notShownResultsCount: number) {
    const hiddenResults = Math.max(0, notShownResultsCount) ? ` and ${notShownResultsCount} hidden results` : '';
    return `Found ${results.length} results${hiddenResults}. Use arrow keys to navigate.`;
}

function prepareItems<T>(items: T[], showResults?: boolean, quickSelectMode?: boolean): (T | {})[] {
    if (!showResults) {
        return [];
    }
    if (!items.length && !quickSelectMode) {
        return [EMPTY_ITEM];
    }
    return items;
}

function prepareGetKey(getKey: GetKey): GetKey {
    return (item, i) => {
        if (item === EMPTY_ITEM) {
            return 'empty';
        }
        return getKey(item, i);
    };
}
